import * as React from 'react';

// Import lifestyle images showing people in different life situations
import coupleInKitchen from '@images_responsive/partnerships/couple-in-kitchen.png';
import womenTalking from '@images_responsive/partnerships/women-talking.png';
import youngManWithLaptop from '@images_responsive/partnerships/young-man-laptop.png';
import youngWomanSmiling from '@images_responsive/partnerships/young-woman-smiling.png';

import {
  HeroSection,
  HeroSectionWrapper,
  HighlightedWord,
  ImageCard,
  LeftSection,
  MainHeading,
  PhotoGrid,
  RightSection,
  StyledImage,
  TextContent,
} from './hero_section.styles';

interface CarouselItem {
  word: string;
  backgroundColor: string;
  highlightedImagePosition: string;
}

const CAROUSEL_ITEMS: CarouselItem[] = [
  {
    word: 'residents',
    backgroundColor: '#1B9089',
    highlightedImagePosition: 'center-large', // coupleInKitchen
  },
  {
    word: 'students',
    backgroundColor: '#FFD078',
    highlightedImagePosition: 'top-center', // youngWomanSmiling
  },
  {
    word: 'seniors & families',
    backgroundColor: '#A1E6E0',
    highlightedImagePosition: 'top-right', // womenTalking
  },
  {
    word: 'employees',
    backgroundColor: '#F79C94',
    highlightedImagePosition: 'bottom-right', // youngManWithLaptop
  },
];

export const HeroSectionComponent: React.FC = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const [isManuallyControlled, setIsManuallyControlled] = React.useState(false);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const resumeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      'matchMedia' in window &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setPrefersReducedMotion(Boolean(reduced));
    if (reduced) {
      setIsPaused(true);
    }
  }, []);

  // Detect touch device capability
  React.useEffect(() => {
    const checkTouchDevice = () => {
      if (typeof window === 'undefined') return false;
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-expect-error - some browsers have this property
        navigator.msMaxTouchPoints > 0
      );
    };
    setIsTouchDevice(checkTouchDevice());
  }, []);

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (isPaused || isManuallyControlled) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % CAROUSEL_ITEMS.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [isPaused, isManuallyControlled]);

  const handleImageClick = (position: string) => {
    const targetIndex = CAROUSEL_ITEMS.findIndex(
      (item) => item.highlightedImagePosition === position,
    );
    if (targetIndex !== -1) {
      setCurrentIndex(targetIndex);
      setIsManuallyControlled(true);

      // On touch devices or when motion is preferred, don't pause automatically
      // Instead, respect user preferences and add a recovery timeout for touch devices
      if (prefersReducedMotion) {
        setIsPaused(true);
      } else if (isTouchDevice) {
        // On touch devices, pause briefly to show the selection, then resume
        setIsPaused(true);

        // Clear any existing timeout
        if (resumeTimeoutRef.current) {
          clearTimeout(resumeTimeoutRef.current);
        }

        // Resume auto-advance after 4 seconds on touch devices
        resumeTimeoutRef.current = setTimeout(() => {
          setIsManuallyControlled(false);
          setIsPaused(false);
        }, 4000);
      } else {
        // Desktop behavior - pause and wait for mouse leave
        setIsPaused(true);
      }
    }
  };

  const handleImageHover = (position: string) => {
    const targetIndex = CAROUSEL_ITEMS.findIndex(
      (item) => item.highlightedImagePosition === position,
    );
    if (targetIndex !== -1) {
      setHoveredIndex(targetIndex);
      setIsPaused(true);
    }
  };

  const handleImageLeave = () => {
    setHoveredIndex(null);

    // Clear any pending resume timeout when mouse interaction occurs
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    setIsManuallyControlled(false);
    setIsPaused(prefersReducedMotion);
  };

  const currentItem = CAROUSEL_ITEMS[currentIndex];

  // Use hovered item if hovering, otherwise use current carousel item
  const displayedItem =
    hoveredIndex !== null ? CAROUSEL_ITEMS[hoveredIndex] : currentItem;

  return (
    <HeroSectionWrapper>
      <HeroSection>
        <LeftSection>
          <TextContent>
            <div
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
              style={{
                position: 'absolute',
                left: '-10000px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
            >
              Now highlighting services for {currentItem.word}
            </div>
            <MainHeading>
              A smarter way to move, store, and simplify life for your{' '}
              <HighlightedWord
                backgroundColor={displayedItem.backgroundColor}
                textColor={
                  displayedItem.word === 'residents' ? '#FFFFFF' : '#000000'
                }
              >
                {displayedItem.word}
              </HighlightedWord>
            </MainHeading>
          </TextContent>
        </LeftSection>
        <RightSection>
          <PhotoGrid>
            <ImageCard
              position="top-center"
              size="small"
              isHighlighted={
                displayedItem.highlightedImagePosition === 'top-center'
              }
              highlightColor={displayedItem.backgroundColor}
              onClick={() => handleImageClick('top-center')}
              onMouseEnter={() => handleImageHover('top-center')}
              onMouseLeave={handleImageLeave}
              style={{ cursor: 'pointer' }}
            >
              <StyledImage
                image={youngWomanSmiling}
                alt="Young Black woman with beautiful curly hair smiling outdoors"
                sizes="(min-width: 768px) 25vw, 50vw"
              />
            </ImageCard>

            <ImageCard
              position="top-right"
              size="small"
              isHighlighted={
                displayedItem.highlightedImagePosition === 'top-right'
              }
              highlightColor={displayedItem.backgroundColor}
              onClick={() => handleImageClick('top-right')}
              onMouseEnter={() => handleImageHover('top-right')}
              onMouseLeave={handleImageLeave}
              style={{ cursor: 'pointer' }}
            >
              <StyledImage
                image={womenTalking}
                alt="Two women having a warm conversation and laughing together"
                sizes="(min-width: 768px) 25vw, 50vw"
              />
            </ImageCard>

            <ImageCard
              position="center-large"
              size="large"
              isHighlighted={
                displayedItem.highlightedImagePosition === 'center-large'
              }
              highlightColor={displayedItem.backgroundColor}
              onClick={() => handleImageClick('center-large')}
              onMouseEnter={() => handleImageHover('center-large')}
              onMouseLeave={handleImageLeave}
              style={{ cursor: 'pointer' }}
            >
              <StyledImage
                image={coupleInKitchen}
                alt="Young couple cooking together in modern kitchen with laptop"
                sizes="(min-width: 768px) 50vw, 100vw"
              />
            </ImageCard>

            <ImageCard
              position="bottom-right"
              size="small"
              isHighlighted={
                displayedItem.highlightedImagePosition === 'bottom-right'
              }
              highlightColor={displayedItem.backgroundColor}
              onClick={() => handleImageClick('bottom-right')}
              onMouseEnter={() => handleImageHover('bottom-right')}
              onMouseLeave={handleImageLeave}
              style={{ cursor: 'pointer' }}
            >
              <StyledImage
                image={youngManWithLaptop}
                alt="Young man with curly hair smiling while working on laptop"
                sizes="(min-width: 768px) 25vw, 50vw"
              />
            </ImageCard>
          </PhotoGrid>
        </RightSection>
      </HeroSection>
    </HeroSectionWrapper>
  );
};
